/* eslint-disable @nx/enforce-module-boundaries */
'use client';

import { If, SplitScreenComponent } from '@payments-checkout/shared/components';
import { initialFingerprint } from '@payments-checkout/shared/helpers/fingerprint';
import { generateUUID } from '@payments-checkout/shared/helpers/uuid/uuid.helpers';
import { analiticaInstance } from '@payments-checkout/shared/plugins/analitica/analitica.events';
import { reportError } from '@payments-checkout/shared/observability';
import {
  resolvePaymentType
} from '@payments-checkout/shared/plugins/analitica/analitica.helpers';
import { usePaymentMethod } from '@payments-checkout/shared/providers';
import { CustomerDataForm } from 'modules/checkout/src/app/components/customerDataForm/customerDataForm.component';
import { useEffect, useState } from 'react';
import { useTranslationProvider } from '../../../app/providers/translationProvider';
import { getDefaultPaymentMethod } from '../../../helpers/getDefaultPaymentMethod.helper';
import { useLoadGlobalWalletExperiment } from '../../../hooks/useLoadGlobalWalletExperiment';
import { useLoadPreselectExperiment } from '../../../hooks/useLoadPreselectExperiment';
import { usePageSharedData } from '../../../hooks/usePageSharedData';
import { usePreselectVariant } from '../../../hooks/usePreselectVariant';
import { ExpirationModal } from '../../expirationModal/expirationModal';
import { LoadingState } from '../../loading';
import { CheckoutDetailsPage } from '../checkoutDetailsPage/checkoutDetailsPage';

type Phones = {
  country_code: string;
  area_code: string;
  number: string;
};

type CustomerType = {
  email: string;
  name: string;
  document: string;
  document_type: string;
  phones: {
    home_phone: Phones;
    mobile_phone: Phones;
  };
};

type CustomerSettings = {
  customer: CustomerType;
};

export function Home() {
  const [antifraudId] = useState(generateUUID());
  const { setPublicKey, setAntifraudId, setThreedSecureEnabled } =
    usePaymentMethod();
  const { isLoading: isPreselectLoading, skipPreselect } = usePreselectVariant();
  useLoadPreselectExperiment();
  useLoadGlobalWalletExperiment();
  const [expirationModal, setExpirationModal] = useState(false);
  const { isReady, paymentLinkIdData, hasAnyDigitalWallet, isDeliveryEnabled } = usePageSharedData();
  const { currentLocale } = useTranslationProvider();

  useEffect(() => {
    const timeout = setTimeout(() => {
      setExpirationModal(true);
    }, 300000);

    return () => {
      clearTimeout(timeout);
    };
  }, []);

  useEffect(() => {
    if (paymentLinkIdData.isFetched && !isPreselectLoading) {
      const acceptedPaymentMethods =
        paymentLinkIdData.data?.payment_settings?.accepted_payment_methods ?? [];

      const defaultPaymentMethod = getDefaultPaymentMethod(
        acceptedPaymentMethods,
        skipPreselect
      );

      analiticaInstance.events.homePage.viewPage(
        paymentLinkIdData.data?.plataform_identifier?.name || '',
        resolvePaymentType(defaultPaymentMethod),
        currentLocale === 'en' ? 'ingles' : 'portugues',
        isDeliveryEnabled
      )
    }
  }, [paymentLinkIdData.isFetched, isPreselectLoading, skipPreselect]);

  useEffect(() => {
    try {
      if (
        paymentLinkIdData.isFetched &&
        paymentLinkIdData.data?.account_settings?.merchant_id
      ) {
        setPublicKey(paymentLinkIdData.data?.account_settings?.public_key);
        setThreedSecureEnabled(
          paymentLinkIdData.data?.payment_settings?.threed_secure_enabled
        );
        setAntifraudId(antifraudId);
        initialFingerprint(
          {
            account_settings: {
              merchant_id: paymentLinkIdData.data?.account_settings.merchant_id,
            },
            organization: paymentLinkIdData.data?.account_settings.organization,
          },
          antifraudId
        );
      }
    } catch (error) {
      reportError(error, {
        source: 'fingerprint_init',
        merchant_id: paymentLinkIdData.data?.account_settings?.merchant_id,
      });
    }
  }, [
    paymentLinkIdData.isFetched,
    paymentLinkIdData.data?.account_settings.merchant_id,
    setPublicKey,
    setAntifraudId,
    antifraudId,
    paymentLinkIdData.data,
    setThreedSecureEnabled,
  ]);

  const isLoading = paymentLinkIdData.isLoading

  if (isLoading) {
    return <LoadingState fullPage />;
  }

  if (!isReady || !paymentLinkIdData.data) {
    return <LoadingState fullPage />;
  }

  return (
    <>
      <If condition={expirationModal}>
        <ExpirationModal />
      </If>
      <SplitScreenComponent
        leftContent={
          <CustomerDataForm
            paymentSettings={paymentLinkIdData.data.payment_settings}
            customerSettings={
              paymentLinkIdData.data
                ?.customer_settings as unknown as CustomerSettings
            }
            hasAnyDigitalWallet={hasAnyDigitalWallet}
          />
        }
        rightContent={
          <CheckoutDetailsPage
            organization={paymentLinkIdData.data.account_settings.organization}
            accountName={paymentLinkIdData.data.account_settings.account_name}
            displayName={paymentLinkIdData.data.account_settings.display_name}
            screen="inicial"
          />
        }
      />
    </>
  );
}
